import type React from "react";

import { useEffect, useRef } from "react";

import { getPinchDistance } from "@/features/request/feedback-tool/feedbackTool/utils";

const MAX_EXTRA_WIDTH = 1200;

export function usePdfPinchZoom({
  contentRef,
  enabled,
  hScrollerRef,
  maxWidth,
  minWidth,
  onZoomEnd,
  vScrollerRef,
}: {
  contentRef: React.RefObject<HTMLElement | null>;
  enabled: boolean;
  hScrollerRef: React.RefObject<HTMLDivElement | null>;
  maxWidth?: null | number;
  minWidth?: null | number;
  onZoomEnd?: (width: number) => void;
  vScrollerRef: React.RefObject<HTMLDivElement | null>;
}): void {
  const maxWidthRef = useRef(maxWidth);
  const minWidthRef = useRef(minWidth);
  const onZoomEndRef = useRef(onZoomEnd);

  maxWidthRef.current = maxWidth;
  minWidthRef.current = minWidth;
  onZoomEndRef.current = onZoomEnd;

  useEffect(() => {
    const hScroller = hScrollerRef.current;
    const scroller = vScrollerRef.current;

    if (!enabled || !hScroller || !scroller) return;

    // Stop scroll from chaining to the page once the scroller hits its
    // top/bottom edge — otherwise pinching near the last pages of a long
    // PDF pans the outer page instead of just zooming.
    scroller.style.overscrollBehavior = "contain";

    let pinchStart: {
      distance: number;
      height: number;
      maxWidth: number;
      minWidth: number;
      normX: number;
      normY: number;
      scrollLeft: number;
      scrollTop: number;
      width: number;
    } | null = null;
    let latestWidth: null | number = null;

    const isInsideBalloon = (e: TouchEvent) =>
      e.target instanceof Element &&
      e.target.closest('[data-name="box-image"]');

    const onTouchStart = (e: TouchEvent) => {
      const content = contentRef.current;

      if (!content || isInsideBalloon(e) || e.touches.length !== 2) return;

      e.preventDefault();
      scroller.style.touchAction = "none";

      const t0 = e.touches.item(0)!;
      const t1 = e.touches.item(1)!;
      const midX = (t0.clientX + t1.clientX) / 2;
      const midY = (t0.clientY + t1.clientY) / 2;
      const rect = content.getBoundingClientRect();
      const minWidth = minWidthRef.current || hScroller.clientWidth;

      pinchStart = {
        distance: getPinchDistance(e.touches),
        height: content.clientHeight,
        maxWidth: maxWidthRef.current || minWidth + MAX_EXTRA_WIDTH,
        minWidth,
        normX: (midX - rect.left) / rect.width,
        normY: (midY - rect.top) / rect.height,
        scrollLeft: hScroller.scrollLeft,
        scrollTop: scroller.scrollTop,
        width: content.clientWidth,
      };
    };

    const onTouchMove = (e: TouchEvent) => {
      const content = contentRef.current;

      if (e.touches.length !== 2 || !pinchStart || !content) return;

      e.preventDefault();
      const ratio = getPinchDistance(e.touches) / pinchStart.distance;
      const newWidth = Math.max(
        pinchStart.minWidth,
        Math.min(pinchStart.maxWidth, pinchStart.width * ratio),
      );

      content.style.width = `${newWidth}px`;
      // Measured, not estimated: fixed per-page padding means height
      // doesn't scale linearly with width once there are many pages,
      // and the drift compounds the further down the document you are.
      const newHeight = content.clientHeight;

      hScroller.scrollLeft =
        pinchStart.scrollLeft +
        pinchStart.normX * (newWidth - pinchStart.width);
      scroller.scrollTop =
        pinchStart.scrollTop +
        pinchStart.normY * (newHeight - pinchStart.height);
      latestWidth = newWidth;
    };

    const onTouchEnd = () => {
      pinchStart = null;
      scroller.style.touchAction = "";

      if (latestWidth !== null) {
        onZoomEndRef.current?.(latestWidth);
        latestWidth = null;
      }
    };

    scroller.addEventListener("touchstart", onTouchStart, { passive: false });
    scroller.addEventListener("touchmove", onTouchMove, { passive: false });
    scroller.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      scroller.removeEventListener("touchstart", onTouchStart);
      scroller.removeEventListener("touchmove", onTouchMove);
      scroller.removeEventListener("touchend", onTouchEnd);
      scroller.style.touchAction = "";
      scroller.style.overscrollBehavior = "";
    };
  }, [enabled, hScrollerRef.current, vScrollerRef.current]);
}
